"use client";

import { c as _c } from "react/compiler-runtime";
import { usePricing } from "@/components/pricing/PricingProvider";
import Image from "next/image";
import Link from "next/link";
import { sendGTMEvent } from "@next/third-parties/google";
import { ChevronLeft, ChevronRight, Heart } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type PointerEvent } from "react";
import type { Car, StaticTileData } from "@/types/cars";
import type { VehicleDetails } from "@/types/vehicle-details";
import { useIsVehicleSaved, useSavedVehicles } from "@/hooks/useSavedVehicles";
import { buildTrackedSellMyCarUrl, useUtmParams } from "@/hooks/useUtmParams";
import { createWishlistEventData, toGAItem, type AnalyticsContext, type VehicleData } from "@/utils/ga4-events";
import { usePaymentPreferences } from "@/hooks/usePaymentPreferences";
import { calculatePaymentEstimate, getEstimatedPaymentRange } from "@/utils/paymentEstimate";
import { formatCurrency } from "@/utils/pricing";
import { schemaVehicleAvailability } from "@/utils/vehicleStatus";
import { getGallerySwipeDirection } from "@/utils/vehicleGallery";
import { buildVehicleGallery } from "@/utils/galleryPromotions";
import { useGalleryPromotions } from "./GalleryPromotionsProvider";
import CollectionSeal from "@/components/collections/CollectionSeal";
import { getMarketingTileContent } from "./marketingTileUtils";
import VehicleImageLabels from "./VehicleImageLabels";
interface SrpVehicleCardProps {
  car: Car | StaticTileData;
  priority?: boolean;
  itemListName?: string;
  listPage?: number;
  listSort?: string;
}
type GalleryDirection = "previous" | "next";
const isStaticTileData = (item: Car | StaticTileData): item is StaticTileData => "textBeforeHighlighted" in item || "highlightedText" in item || "contactNumber" in item;
const statusLabel = (status?: Car["status"]) => {
  switch (status) {
    case "deposit":
      return "On Hold";
    case "coming soon":
      return "Coming Soon";
    case "sold":
      return "Sold";
    case "sales pending":
    case "cancelled":
      return "Sale Pending";
    default:
      return null;
  }
};
function MarketingTile(t0) {
  const $ = _c(43);
  const {
    data
  } = t0;
  let t1;
  if ($[0] !== data) {
    t1 = getMarketingTileContent(data);
    $[0] = data;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tile = t1;
  const utmParams = useUtmParams();
  let emphasizedTitle;
  let t2;
  if ($[2] !== tile.title) {
    const titleWords = tile.title.trim().split(/\s+/);
    emphasizedTitle = titleWords.pop() || tile.title;
    t2 = titleWords.join(" ");
    $[2] = tile.title;
    $[3] = emphasizedTitle;
    $[4] = t2;
  } else {
    emphasizedTitle = $[3];
    t2 = $[4];
  }
  const titleLead = t2;
  const isBrightTile = tile.variant === "trade_appraisal" || tile.variant === "financing";
  let t3;
  bb0: {
    if (tile.variant === "trade_appraisal" || tile.variant === "sell_my_car") {
      const t4 = tile.ctaSource || "cars_tile_sell_my_car";
      let t5;
      if ($[5] !== t4 || $[6] !== utmParams) {
        t5 = buildTrackedSellMyCarUrl({
          ctaSource: t4,
          utmParams
        });
        $[5] = t4;
        $[6] = utmParams;
        $[7] = t5;
      } else {
        t5 = $[7];
      }
      t3 = t5;
      break bb0;
    }
    t3 = data.tileLink || (tile.variant === "financing" ? "/pre-qualify" : "tel:8008447227");
  }
  const href = t3;
  let t4;
  if ($[8] !== tile.creativeSlot || $[9] !== tile.promotionId || $[10] !== tile.promotionName || $[11] !== tile.userIntent || $[12] !== tile.variant) {
    t4 = () => {
      sendGTMEvent({
        event: tile.variant === "support" ? "contact_tile_click" : "select_promotion",
        promotion_id: tile.promotionId,
        promotion_name: tile.promotionName,
        creative_slot: tile.creativeSlot,
        page_section: "inventory_marketing_tile",
        user_intent: tile.userIntent
      });
    };
    $[8] = tile.creativeSlot;
    $[9] = tile.promotionId;
    $[10] = tile.promotionName;
    $[11] = tile.userIntent;
    $[12] = tile.variant;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleClick = t4;
  const t5 = `t-block t-text-[10px] t-font-black t-uppercase t-tracking-[0.15em] ${isBrightTile ? "t-text-automotive-secondary" : "t-text-white/80"}`;
  let t6;
  if ($[14] !== t5 || $[15] !== tile.subtitle) {
    t6 = <span className={t5}>{tile.subtitle}</span>;
    $[14] = t5;
    $[15] = tile.subtitle;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== titleLead) {
    t7 = titleLead && <>{titleLead}<br /></>;
    $[17] = titleLead;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = `t-not-italic ${isBrightTile ? "t-text-automotive-primary" : "t-text-brand-highlighter"}`;
  let t9;
  if ($[19] !== emphasizedTitle || $[20] !== t8) {
    t9 = <em className={t8}>{emphasizedTitle}</em>;
    $[19] = emphasizedTitle;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t7 || $[23] !== t9) {
    t10 = <span className="t-my-4 t-block t-text-[32px] t-font-black t-leading-[1.02] t-tracking-[-0.05em] sm:t-my-[18px] sm:t-text-[37px]">{t7}{t9}</span>;
    $[22] = t7;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = `t-mb-6 t-block t-max-w-[280px] t-text-sm t-leading-[1.55] ${isBrightTile ? "t-text-automotive-dark/80" : "t-text-white/85"}`;
  let t12;
  if ($[25] !== t11 || $[26] !== tile.description) {
    t12 = <span className={t11}>{tile.description}</span>;
    $[25] = t11;
    $[26] = tile.description;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== tile.buttonText) {
    t13 = <span>{tile.buttonText}</span>;
    $[28] = tile.buttonText;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span aria-hidden="true" className="t-transition-transform t-duration-200 group-hover:t-translate-x-1">→</span>;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t13) {
    t15 = <span className="t-inline-flex t-items-center t-gap-2 t-text-[13px] t-font-black">{t13}{t14}</span>;
    $[31] = t13;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t10 || $[34] !== t12 || $[35] !== t15 || $[36] !== t6) {
    t16 = <>{t6}{t10}{t12}{t15}</>;
    $[33] = t10;
    $[34] = t12;
    $[35] = t15;
    $[36] = t6;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const content = t16;
  const className = `t-group t-flex t-h-full t-min-h-[400px] t-flex-col t-justify-end t-overflow-hidden t-rounded t-border t-p-7 t-no-underline t-transition-[transform,box-shadow,border-color] t-duration-200 hover:-t-translate-y-0.5 hover:t-shadow-[0_12px_28px_rgba(28,29,31,0.14)] focus:t-outline-none focus:t-ring-2 focus:t-ring-offset-2 sm:t-min-h-[440px] sm:t-p-[34px] ${isBrightTile ? "t-border-brand-violet/20 t-bg-brand-highlighter t-text-automotive-dark focus:t-ring-brand-violet" : "t-border-brand-violet t-bg-gradient-to-br t-from-brand-violet-dark t-to-brand-violet t-text-white focus:t-ring-brand-highlighter"}`;
  let t17;
  if ($[38] !== className || $[39] !== content || $[40] !== handleClick || $[41] !== href) {
    t17 = href.startsWith("tel:") ? <a href={href} className={className} onClick={handleClick}>{content}</a> : <Link href={href} prefetch={false} className={className} onClick={handleClick}>{content}</Link>;
    $[38] = className;
    $[39] = content;
    $[40] = handleClick;
    $[41] = href;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  return t17;
}
function VehicleCard(t0) {
  const $ = _c(206);
  const {
    car,
    priority: t1,
    itemListName: t2,
    listPage: t3,
    listSort: t4
  } = t0;
  const priority = t1 === undefined ? false : t1;
  const itemListName = t2 === undefined ? "search_results" : t2;
  const listPage = t3 === undefined ? 1 : t3;
  const listSort = t4 === undefined ? "specials" : t4;
  const {
    getNoHagglePricing
  } = usePricing();
  let t5;
  if ($[0] !== car.images || $[1] !== car.photoUrls) {
    t5 = car.photoUrls?.length ? car.photoUrls : car.images || [];
    $[0] = car.images;
    $[1] = car.photoUrls;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const rawImages = t5;
  const promotions = useGalleryPromotions();
  const t6 = `${car.year || ""} ${car.make || ""} ${car.model || ""}`;
  let t7;
  if ($[3] !== t6) {
    t7 = t6.trim();
    $[3] = t6;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const carTitle = t7;
  let t8;
  if ($[5] !== car.make || $[6] !== car.model || $[7] !== car.price || $[8] !== carTitle || $[9] !== promotions || $[10] !== rawImages) {
    t8 = buildVehicleGallery(rawImages, promotions, carTitle, {
      price: car.price,
      make: car.make,
      model: car.model
    });
    $[5] = car.make;
    $[6] = car.model;
    $[7] = car.price;
    $[8] = carTitle;
    $[9] = promotions;
    $[10] = rawImages;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const images = t8;
  const [photoError, setPhotoError] = useState(false);
  const [visibleIndex, setVisibleIndex] = useState(0);
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [0];
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const [visitedIndices, setVisitedIndices] = useState(t9);
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = new Set();
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const loadedImages = useRef(t10);
  const requestedIndex = useRef(0);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const saveFeedbackTimeoutRef = useRef(null);
  const swipeResetTimeoutRef = useRef(null);
  const pointerStartRef = useRef(null);
  const suppressImageLinkRef = useRef(false);
  let t11;
  if ($[14] !== car.price || $[15] !== getNoHagglePricing) {
    t11 = getNoHagglePricing(car.price);
    $[14] = car.price;
    $[15] = getNoHagglePricing;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const pricing = t11;
  const noHagglePrice = pricing?.noHagglePrice ?? car.price;
  let t12;
  if ($[17] !== noHagglePrice || $[18] !== pricing) {
    t12 = pricing !== null && Number.isFinite(noHagglePrice) && noHagglePrice > 0;
    $[17] = noHagglePrice;
    $[18] = pricing;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const hasOffer = t12;
  const {
    preferences: paymentPreferences
  } = usePaymentPreferences();
  let t13;
  bb0: {
    if (paymentPreferences?.customized) {
      let t14;
      if ($[20] !== noHagglePrice || $[21] !== paymentPreferences.down || $[22] !== paymentPreferences.term || $[23] !== paymentPreferences.tier) {
        t14 = calculatePaymentEstimate({
          advertisedPrice: noHagglePrice,
          downPayment: paymentPreferences.down,
          termMonths: paymentPreferences.term,
          creditProfileId: paymentPreferences.tier
        });
        $[20] = noHagglePrice;
        $[21] = paymentPreferences.down;
        $[22] = paymentPreferences.term;
        $[23] = paymentPreferences.tier;
        $[24] = t14;
      } else {
        t14 = $[24];
      }
      const customized = t14;
      if (customized) {
        let t15;
        if ($[25] !== customized || $[26] !== paymentPreferences.down || $[27] !== paymentPreferences.term || $[28] !== paymentPreferences.tier) {
          t15 = {
            ...customized,
            customized: true,
            downPayment: paymentPreferences.down,
            termMonths: paymentPreferences.term,
            creditProfileId: paymentPreferences.tier
          };
          $[25] = customized;
          $[26] = paymentPreferences.down;
          $[27] = paymentPreferences.term;
          $[28] = paymentPreferences.tier;
          $[29] = t15;
        } else {
          t15 = $[29];
        }
        t13 = t15;
        break bb0;
      }
    }
    let t14;
    if ($[30] !== car.year || $[31] !== noHagglePrice) {
      t14 = getEstimatedPaymentRange(noHagglePrice, car.year);
      $[30] = car.year;
      $[31] = noHagglePrice;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    const sample = t14;
    if (!sample) {
      t13 = null;
      break bb0;
    }
    let t15;
    if ($[33] !== sample) {
      t15 = {
        ...sample,
        customized: false,
        downPayment: null,
        creditProfileId: "excellent" as const
      };
      $[33] = sample;
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    t13 = t15;
  }
  const paymentEstimate = t13;
  const carLink = `/cars/${car.vin}`;
  const isSaved = useIsVehicleSaved(car.vin);
  const {
    toggleSave
  } = useSavedVehicles();
  let t14;
  if ($[35] !== car.status) {
    t14 = statusLabel(car.status);
    $[35] = car.status;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const ribbon = t14;
  let t15;
  if ($[37] !== car.make || $[38] !== car.model || $[39] !== car.vin || $[40] !== car.year || $[41] !== carTitle || $[42] !== noHagglePrice) {
    t15 = {
      vin: car.vin,
      name: carTitle,
      price: noHagglePrice,
      make: car.make,
      model: car.model,
      year: car.year
    };
    $[37] = car.make;
    $[38] = car.model;
    $[39] = car.vin;
    $[40] = car.year;
    $[41] = carTitle;
    $[42] = noHagglePrice;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const vehicleData = t15;
  let t16;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      buttonLocation: "car_card"
    };
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  const analyticsContext = t16;
  let t17;
  if ($[45] !== car.odometer) {
    t17 = car.odometer ? `${new Intl.NumberFormat("en-US").format(car.odometer)} miles` : "Mileage unavailable";
    $[45] = car.odometer;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  const mileageLabel = t17;
  const totalPhotoCount = images.length;
  let t18;
  let t19;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => () => {
      if (saveFeedbackTimeoutRef.current) {
        window.clearTimeout(saveFeedbackTimeoutRef.current);
      }
      if (swipeResetTimeoutRef.current) {
        window.clearTimeout(swipeResetTimeoutRef.current);
      }
    };
    t19 = [];
    $[47] = t18;
    $[48] = t19;
  } else {
    t18 = $[47];
    t19 = $[48];
  }
  useEffect(t18, t19);
  let t20;
  if ($[49] !== itemListName || $[50] !== listPage || $[51] !== listSort || $[52] !== vehicleData) {
    t20 = () => {
      sendGTMEvent({
        event: "select_item",
        item_list_name: itemListName,
        items: [toGAItem(vehicleData)],
        list_page: listPage,
        list_sort: listSort
      });
    };
    $[49] = itemListName;
    $[50] = listPage;
    $[51] = listSort;
    $[52] = vehicleData;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  const handleVehicleClick = t20;
  let t21;
  if ($[54] !== car.bodyStyle || $[55] !== car.dealerStockId || $[56] !== car.doors || $[57] !== car.drivetrain || $[58] !== car.engine || $[59] !== car.extColor || $[60] !== car.fuel || $[61] !== car.interiorColor || $[62] !== car.make || $[63] !== car.model || $[64] !== car.numberOfPhotos || $[65] !== car.odometer || $[66] !== car.price || $[67] !== car.status || $[68] !== car.stockId || $[69] !== car.transmission || $[70] !== car.trim || $[71] !== car.vin || $[72] !== car.year || $[73] !== isSaved || $[74] !== rawImages || $[75] !== toggleSave || $[76] !== vehicleData) {
    t21 = async () => {
      const stockId = car.stockId || car.dealerStockId;
      const vehicleToSave = {
        vin: car.vin,
        make: car.make,
        model: car.model,
        year: car.year,
        price: car.price,
        odometer: car.odometer,
        images: rawImages,
        photoUrls: rawImages,
        numberOfPhotos: car.numberOfPhotos,
        ...(car.trim && {
          trim: car.trim
        }),
        ...(car.status && {
          status: car.status
        }),
        ...(stockId && {
          stockId
        }),
        ...(car.engine && {
          engine: car.engine
        }),
        ...(car.transmission && {
          transmission: car.transmission
        }),
        ...(car.drivetrain && {
          drivetrain: car.drivetrain
        }),
        ...(car.fuel && {
          fuel: car.fuel
        }),
        ...(car.extColor && {
          extColor: car.extColor
        }),
        ...(car.interiorColor && {
          interiorColor: car.interiorColor
        }),
        ...(car.bodyStyle && {
          bodyStyle: car.bodyStyle
        }),
        ...(car.doors && {
          doors: car.doors
        })
      };
      if (await toggleSave(vehicleToSave)) {
        setShowSaveSuccess(true);
        if (saveFeedbackTimeoutRef.current) {
          window.clearTimeout(saveFeedbackTimeoutRef.current);
        }
        saveFeedbackTimeoutRef.current = window.setTimeout(() => setShowSaveSuccess(false), 1800);
        sendGTMEvent({
          event: isSaved ? "remove_from_wishlist" : "add_to_wishlist",
          ...createWishlistEventData(vehicleData, analyticsContext)
        });
      }
    };
    $[54] = car.bodyStyle;
    $[55] = car.dealerStockId;
    $[56] = car.doors;
    $[57] = car.drivetrain;
    $[58] = car.engine;
    $[59] = car.extColor;
    $[60] = car.fuel;
    $[61] = car.interiorColor;
    $[62] = car.make;
    $[63] = car.model;
    $[64] = car.numberOfPhotos;
    $[65] = car.odometer;
    $[66] = car.price;
    $[67] = car.status;
    $[68] = car.stockId;
    $[69] = car.transmission;
    $[70] = car.trim;
    $[71] = car.vin;
    $[72] = car.year;
    $[73] = isSaved;
    $[74] = rawImages;
    $[75] = toggleSave;
    $[76] = vehicleData;
    $[77] = t21;
  } else {
    t21 = $[77];
  }
  const handleSaveVehicle = t21;
  let t22;
  if ($[78] !== images) {
    t22 = direction => {
      if (images.length <= 1) {
        return;
      }
      const nextIndex = (requestedIndex.current + (direction === "next" ? 1 : -1) + images.length) % images.length;
      setPhotoError(false);
      requestedIndex.current = nextIndex;
      setVisitedIndices(current => current.includes(nextIndex) ? current : [...current, nextIndex]);
      if (loadedImages.current.has(images[nextIndex].src)) {
        setVisibleIndex(nextIndex);
      }
    };
    $[78] = images;
    $[79] = t22;
  } else {
    t22 = $[79];
  }
  const transitionGallery = t22;
  let t23;
  if ($[80] !== transitionGallery) {
    t23 = () => transitionGallery("previous");
    $[80] = transitionGallery;
    $[81] = t23;
  } else {
    t23 = $[81];
  }
  const showPrevious = t23;
  let t24;
  if ($[82] !== transitionGallery) {
    t24 = () => transitionGallery("next");
    $[82] = transitionGallery;
    $[83] = t24;
  } else {
    t24 = $[83];
  }
  const showNext = t24;
  let t25;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = event => {
      if (!event.isPrimary || event.pointerType === "mouse" && event.button !== 0) {
        return;
      }
      pointerStartRef.current = {
        x: event.clientX,
        y: event.clientY
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    };
    $[84] = t25;
  } else {
    t25 = $[84];
  }
  const handleGalleryPointerDown = t25;
  let t26;
  if ($[85] !== images.length || $[86] !== showNext || $[87] !== showPrevious) {
    t26 = event_0 => {
      const start = pointerStartRef.current;
      pointerStartRef.current = null;
      if (event_0.currentTarget.hasPointerCapture(event_0.pointerId)) {
        event_0.currentTarget.releasePointerCapture(event_0.pointerId);
      }
      if (!start || images.length <= 1) {
        return;
      }
      const direction_0 = getGallerySwipeDirection({
        startX: start.x,
        startY: start.y,
        endX: event_0.clientX,
        endY: event_0.clientY
      });
      if (!direction_0) {
        return;
      }
      suppressImageLinkRef.current = true;
      if (swipeResetTimeoutRef.current) {
        window.clearTimeout(swipeResetTimeoutRef.current);
      }
      swipeResetTimeoutRef.current = window.setTimeout(() => {
        suppressImageLinkRef.current = false;
      }, 400);
      if (direction_0 === "next") {
        showNext();
      } else {
        showPrevious();
      }
    };
    $[85] = images.length;
    $[86] = showNext;
    $[87] = showPrevious;
    $[88] = t26;
  } else {
    t26 = $[88];
  }
  const handleGalleryPointerUp = t26;
  let t27;
  if ($[89] !== handleVehicleClick) {
    t27 = event_1 => {
      if (suppressImageLinkRef.current) {
        event_1.preventDefault();
        suppressImageLinkRef.current = false;
        return;
      }
      handleVehicleClick();
    };
    $[89] = handleVehicleClick;
    $[90] = t27;
  } else {
    t27 = $[90];
  }
  const handleImageLinkClick = t27;
  const t28 = `https://thiscar.com${carLink}#vehicle`;
  let t29;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => {
      pointerStartRef.current = null;
    };
    $[91] = t29;
  } else {
    t29 = $[91];
  }
  const t30 = images.length > 1 ? `View ${carTitle}. Swipe left or right to browse ${images.length} photos.` : `View ${carTitle}`;
  let t31;
  if ($[92] !== images || $[93] !== priority || $[94] !== visibleIndex || $[95] !== visitedIndices) {
    t31 = visitedIndices.map(index => {
      const image = images[index];
      if (!image) {
        return null;
      }
      return <Image key={image.id} src={image.src} alt={index === visibleIndex ? image.alt : ""} aria-hidden={index !== visibleIndex} fill={true} className={`t-pointer-events-none t-transition-opacity t-duration-[360ms] t-ease-in-out motion-reduce:t-transition-none ${image.kind === "promotion" ? "t-object-contain t-bg-gray-950" : "t-object-cover"} ${index === visibleIndex ? "t-opacity-100" : "t-opacity-0"}`} priority={priority && index === 0} loading={priority && index === 0 ? undefined : index === 0 ? "lazy" : "eager"} {...priority && index === 0 ? {
        fetchPriority: "high" as const
      } : {}} sizes="(max-width: 767px) 100vw, (max-width: 1279px) 50vw, 33vw" itemProp={index === visibleIndex && image.kind === "vehicle" ? "image" : undefined} onError={() => {
        if (requestedIndex.current === index) {
          setPhotoError(true);
        }
      }} onLoad={() => {
        loadedImages.current.add(image.src);
        if (requestedIndex.current === index) {
          setVisibleIndex(index);
          setPhotoError(false);
        }
      }} draggable={false} />;
    });
    $[92] = images;
    $[93] = priority;
    $[94] = visibleIndex;
    $[95] = visitedIndices;
    $[96] = t31;
  } else {
    t31 = $[96];
  }
  let t32;
  if ($[97] !== carLink || $[98] !== handleGalleryPointerUp || $[99] !== handleImageLinkClick || $[100] !== priority || $[101] !== t30 || $[102] !== t31) {
    t32 = <Link href={carLink} className="t-absolute t-inset-0 t-block t-touch-pan-y t-select-none t-no-underline focus-visible:t-outline focus-visible:t-outline-4 focus-visible:t-outline-offset-[-4px] focus-visible:t-outline-brand-violet" prefetch={priority} onClick={handleImageLinkClick} onPointerDown={handleGalleryPointerDown} onPointerUp={handleGalleryPointerUp} onPointerCancel={t29} draggable={false} aria-label={t30}>{t31}</Link>;
    $[97] = carLink;
    $[98] = handleGalleryPointerUp;
    $[99] = handleImageLinkClick;
    $[100] = priority;
    $[101] = t30;
    $[102] = t31;
    $[103] = t32;
  } else {
    t32 = $[103];
  }
  let t33;
  if ($[104] !== ribbon) {
    t33 = <VehicleImageLabels statusLabel={ribbon} />;
    $[104] = ribbon;
    $[105] = t33;
  } else {
    t33 = $[105];
  }
  const t34 = `t-absolute t-right-3 t-top-3 t-z-30 t-flex t-h-11 t-w-11 t-items-center t-justify-center t-rounded-full t-border t-border-white/80 t-shadow-md t-backdrop-blur t-transition-all hover:t-scale-105 ${isSaved ? "t-bg-brand-rose t-text-white" : "t-bg-white/90 t-text-brand-ink-muted hover:t-bg-white hover:t-text-brand-rose"} ${showSaveSuccess ? "t-scale-110" : ""}`;
  const t35 = isSaved ? "Remove from saved vehicles" : "Save vehicle";
  const t36 = `t-h-5 t-w-5 ${isSaved ? "t-fill-current" : ""}`;
  let t37;
  if ($[106] !== t36) {
    t37 = <Heart className={t36} aria-hidden="true" />;
    $[106] = t36;
    $[107] = t37;
  } else {
    t37 = $[107];
  }
  let t38;
  if ($[108] !== handleSaveVehicle || $[109] !== t34 || $[110] !== t35 || $[111] !== t37) {
    t38 = <button type="button" onClick={handleSaveVehicle} className={t34} aria-label={t35}>{t37}</button>;
    $[108] = handleSaveVehicle;
    $[109] = t34;
    $[110] = t35;
    $[111] = t37;
    $[112] = t38;
  } else {
    t38 = $[112];
  }
  let t39;
  if ($[113] !== carTitle || $[114] !== images.length || $[115] !== showNext || $[116] !== showPrevious) {
    t39 = images.length > 1 && <><button type="button" onClick={showPrevious} className="t-absolute t-left-3 t-top-1/2 t-z-20 t-hidden md:t-flex t-h-11 t-w-11 t--translate-y-1/2 t-items-center t-justify-center t-rounded-full t-border t-border-white/70 t-bg-white/90 t-text-automotive-dark t-shadow-md t-backdrop-blur-sm t-transition-opacity motion-reduce:t-transition-none sm:t-opacity-0 group-hover:t-opacity-100 focus:t-opacity-100 focus-visible:t-outline focus-visible:t-outline-2 focus-visible:t-outline-offset-2 focus-visible:t-outline-brand-violet" aria-label={`Previous image of ${carTitle}`}><ChevronLeft className="t-h-5 t-w-5" aria-hidden="true" /></button><button type="button" onClick={showNext} className="t-absolute t-right-3 t-top-1/2 t-z-20 t-hidden md:t-flex t-h-11 t-w-11 t--translate-y-1/2 t-items-center t-justify-center t-rounded-full t-border t-border-white/70 t-bg-white/90 t-text-automotive-dark t-shadow-md t-backdrop-blur-sm t-transition-opacity motion-reduce:t-transition-none sm:t-opacity-0 group-hover:t-opacity-100 focus:t-opacity-100 focus-visible:t-outline focus-visible:t-outline-2 focus-visible:t-outline-offset-2 focus-visible:t-outline-brand-violet" aria-label={`Next image of ${carTitle}`}><ChevronRight className="t-h-5 t-w-5" aria-hidden="true" /></button></>;
    $[113] = carTitle;
    $[114] = images.length;
    $[115] = showNext;
    $[116] = showPrevious;
    $[117] = t39;
  } else {
    t39 = $[117];
  }
  const t40 = visibleIndex + 1;
  let t41;
  if ($[118] !== t40 || $[119] !== totalPhotoCount) {
    t41 = <span aria-hidden="true">{t40} / {totalPhotoCount}</span>;
    $[118] = t40;
    $[119] = totalPhotoCount;
    $[120] = t41;
  } else {
    t41 = $[120];
  }
  const t42 = photoError ? "That photo could not be loaded. Try another photo. " : "";
  const t43 = visibleIndex + 1;
  const t44 = images[visibleIndex]?.alt;
  let t45;
  if ($[121] !== t42 || $[122] !== t43 || $[123] !== t44 || $[124] !== totalPhotoCount) {
    t45 = <span className="t-sr-only">{t42}Photo {t43} of {totalPhotoCount}: {t44}</span>;
    $[121] = t42;
    $[122] = t43;
    $[123] = t44;
    $[124] = totalPhotoCount;
    $[125] = t45;
  } else {
    t45 = $[125];
  }
  let t46;
  if ($[126] !== t41 || $[127] !== t45) {
    t46 = <span className="t-absolute t-bottom-3 t-right-3 t-z-20 t-rounded-full t-bg-automotive-dark/75 t-px-2.5 t-py-1 t-text-[11px] t-font-semibold t-tabular-nums t-text-white t-backdrop-blur-sm" role="status" aria-live="polite" aria-atomic="true">{t41}{t45}</span>;
    $[126] = t41;
    $[127] = t45;
    $[128] = t46;
  } else {
    t46 = $[128];
  }
  let t47;
  if ($[129] !== car.segmentBadge || $[130] !== car.vin) {
    t47 = car.segmentBadge && <div className="t-absolute t-bottom-3 t-left-3 t-z-30"><CollectionSeal badge={car.segmentBadge} placement="card" vehicleVin={car.vin} /></div>;
    $[129] = car.segmentBadge;
    $[130] = car.vin;
    $[131] = t47;
  } else {
    t47 = $[131];
  }
  let t48;
  if ($[132] !== t32 || $[133] !== t33 || $[134] !== t38 || $[135] !== t39 || $[136] !== t46 || $[137] !== t47) {
    t48 = <div className="t-relative t-aspect-[1024/683] t-overflow-hidden t-bg-brand-surface">{t32}{t33}{t38}{t39}{t46}{t47}</div>;
    $[132] = t32;
    $[133] = t33;
    $[134] = t38;
    $[135] = t39;
    $[136] = t46;
    $[137] = t47;
    $[138] = t48;
  } else {
    t48 = $[138];
  }
  let t49;
  if ($[139] !== carTitle) {
    t49 = <h3 className="t-m-0 t-truncate t-text-xl t-font-semibold t-leading-tight t-tracking-[-0.02em] t-text-automotive-dark group-hover:t-text-brand-violet" itemProp="name">{carTitle}</h3>;
    $[139] = carTitle;
    $[140] = t49;
  } else {
    t49 = $[140];
  }
  const t50 = car.trim || "Used vehicle";
  let t51;
  if ($[141] !== mileageLabel || $[142] !== t50) {
    t51 = <p className="t-m-0 t-mt-1.5 t-truncate t-text-sm t-text-brand-gray-medium">{t50} · {mileageLabel}</p>;
    $[141] = mileageLabel;
    $[142] = t50;
    $[143] = t51;
  } else {
    t51 = $[143];
  }
  let t52;
  if ($[144] !== car.dealerStockId || $[145] !== car.stockId) {
    t52 = (car.stockId?.trim() || car.dealerStockId?.trim()) && <p className="t-m-0 t-mt-1.5 t-break-words t-text-xs t-text-brand-gray-medium">Stock #{car.stockId?.trim() || car.dealerStockId?.trim()}</p>;
    $[144] = car.dealerStockId;
    $[145] = car.stockId;
    $[146] = t52;
  } else {
    t52 = $[146];
  }
  let t53;
  if ($[147] !== paymentEstimate) {
    t53 = paymentEstimate && <div className="t-mt-5 t-border-l-4 t-border-brand-highlighter t-bg-brand-lavender/35 t-px-3.5 t-py-3"><span className="t-block t-text-[10px] t-font-bold t-uppercase t-tracking-[0.12em] t-text-brand-violet-dark">{paymentEstimate.customized ? "Your est. payment range" : "Est. payment range"}</span><span className="t-mt-2 t-block t-whitespace-nowrap t-text-[clamp(1.25rem,6vw,1.5rem)] t-font-bold t-leading-none t-tracking-[-0.03em] t-text-brand-violet-dark">{formatCurrency(paymentEstimate.minimumMonthlyPayment)}–{formatCurrency(paymentEstimate.maximumMonthlyPayment)}<span className="t-text-base">/mo</span></span><span className="t-mt-2 t-block t-text-[9px] t-leading-[1.35] t-text-brand-ink">{paymentEstimate.customized && paymentEstimate.downPayment !== null ? `Based on your selections: ${formatCurrency(paymentEstimate.downPayment)} down, ${paymentEstimate.termMonths} months and ${paymentEstimate.creditProfileId} credit.` : `Estimate based on 10% down, ${paymentEstimate.termMonths} months and excellent credit.`}</span></div>;
    $[147] = paymentEstimate;
    $[148] = t53;
  } else {
    t53 = $[148];
  }
  const t54 = hasOffer ? "offers" : undefined;
  const t55 = hasOffer ? "https://schema.org/Offer" : undefined;
  const t56 = hasOffer ? `https://thiscar.com${carLink}#offer` : undefined;
  let t57;
  if ($[149] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <span className="t-block t-text-[10px] t-font-bold t-uppercase t-tracking-[0.12em] t-text-brand-gray-medium">No-haggle price</span>;
    $[149] = t57;
  } else {
    t57 = $[149];
  }
  const t58 = hasOffer ? "price" : undefined;
  let t59;
  if ($[150] !== hasOffer || $[151] !== noHagglePrice) {
    t59 = hasOffer ? noHagglePrice.toString() : undefined;
    $[150] = hasOffer;
    $[151] = noHagglePrice;
    $[152] = t59;
  } else {
    t59 = $[152];
  }
  let t60;
  if ($[153] !== noHagglePrice) {
    t60 = formatCurrency(noHagglePrice);
    $[153] = noHagglePrice;
    $[154] = t60;
  } else {
    t60 = $[154];
  }
  let t61;
  if ($[155] !== t58 || $[156] !== t59 || $[157] !== t60) {
    t61 = <span className="t-mt-1.5 t-block t-text-2xl t-font-bold t-leading-none t-text-automotive-dark" itemProp={t58} content={t59}>{t60}</span>;
    $[155] = t58;
    $[156] = t59;
    $[157] = t60;
    $[158] = t61;
  } else {
    t61 = $[158];
  }
  let t62;
  if ($[159] !== car.status || $[160] !== hasOffer) {
    t62 = hasOffer && <><meta itemProp="priceCurrency" content="USD" /><meta itemProp="itemCondition" content="https://schema.org/UsedCondition" /><meta itemProp="availability" content={schemaVehicleAvailability(car.status)} /></>;
    $[159] = car.status;
    $[160] = hasOffer;
    $[161] = t62;
  } else {
    t62 = $[161];
  }
  let t63;
  if ($[162] !== hasOffer || $[163] !== t54 || $[164] !== t55 || $[165] !== t56 || $[166] !== t61 || $[167] !== t62) {
    t63 = <div className="t-mt-4" itemProp={t54} itemScope={hasOffer} itemType={t55} itemID={t56}>{t57}{t61}{t62}</div>;
    $[162] = hasOffer;
    $[163] = t54;
    $[164] = t55;
    $[165] = t56;
    $[166] = t61;
    $[167] = t62;
    $[168] = t63;
  } else {
    t63 = $[168];
  }
  let t64;
  if ($[169] !== car.make) {
    t64 = <meta itemProp="brand" content={car.make} />;
    $[169] = car.make;
    $[170] = t64;
  } else {
    t64 = $[170];
  }
  let t65;
  if ($[171] !== car.model) {
    t65 = <meta itemProp="model" content={car.model} />;
    $[171] = car.model;
    $[172] = t65;
  } else {
    t65 = $[172];
  }
  const t66 = String(car.year);
  let t67;
  if ($[173] !== t66) {
    t67 = <meta itemProp="vehicleModelDate" content={t66} />;
    $[173] = t66;
    $[174] = t67;
  } else {
    t67 = $[174];
  }
  let t68;
  if ($[175] !== car.vin) {
    t68 = <meta itemProp="vehicleIdentificationNumber" content={car.vin} />;
    $[175] = car.vin;
    $[176] = t68;
  } else {
    t68 = $[176];
  }
  let t69;
  if ($[177] !== car.odometer) {
    t69 = Number.isFinite(car.odometer) && car.odometer >= 0 && <span className="t-sr-only" itemProp="mileageFromOdometer" itemScope={true} itemType="https://schema.org/QuantitativeValue"><meta itemProp="value" content={String(car.odometer)} /><meta itemProp="unitCode" content="SMI" /></span>;
    $[177] = car.odometer;
    $[178] = t69;
  } else {
    t69 = $[178];
  }
  let t70;
  if ($[179] !== car.bodyStyle) {
    t70 = car.bodyStyle && <meta itemProp="bodyType" content={car.bodyStyle} />;
    $[179] = car.bodyStyle;
    $[180] = t70;
  } else {
    t70 = $[180];
  }
  let t71;
  if ($[181] !== car.extColor) {
    t71 = car.extColor && <meta itemProp="color" content={car.extColor} />;
    $[181] = car.extColor;
    $[182] = t71;
  } else {
    t71 = $[182];
  }
  let t72;
  if ($[183] !== carLink || $[184] !== handleVehicleClick || $[185] !== priority || $[186] !== t49 || $[187] !== t51 || $[188] !== t52 || $[189] !== t53 || $[190] !== t63 || $[191] !== t64 || $[192] !== t65 || $[193] !== t67 || $[194] !== t68 || $[195] !== t69 || $[196] !== t70 || $[197] !== t71) {
    t72 = <Link href={carLink} className="t-flex t-flex-1 t-flex-col t-px-5 t-pb-4 t-pt-5 t-text-inherit t-no-underline" prefetch={priority} onClick={handleVehicleClick} itemProp="url">{t49}{t51}{t52}{t53}{t63}{t64}{t65}{t67}{t68}{t69}{t70}{t71}</Link>;
    $[183] = carLink;
    $[184] = handleVehicleClick;
    $[185] = priority;
    $[186] = t49;
    $[187] = t51;
    $[188] = t52;
    $[189] = t53;
    $[190] = t63;
    $[191] = t64;
    $[192] = t65;
    $[193] = t67;
    $[194] = t68;
    $[195] = t69;
    $[196] = t70;
    $[197] = t71;
    $[198] = t72;
  } else {
    t72 = $[198];
  }
  let t73;
  if ($[199] === Symbol.for("react.memo_cache_sentinel")) {
    t73 = <div className="t-mx-5 t-mt-auto t-flex t-items-center t-gap-2 t-border-t t-border-brand-violet/10 t-py-3.5 t-text-sm t-font-semibold t-text-brand-violet"><span className="t-flex t-h-5 t-w-5 t-items-center t-justify-center t-rounded-full t-bg-brand-highlighter t-text-xs t-font-black t-text-automotive-dark" aria-hidden="true">✓</span>No dealer fees</div>;
    $[199] = t73;
  } else {
    t73 = $[199];
  }
  let t74;
  if ($[200] !== t48 || $[201] !== t72) {
    t74 = <div className="t-group t-flex t-h-full t-flex-col t-overflow-hidden t-rounded-lg t-border t-border-brand-violet/15 t-bg-white t-transition-[border-color,box-shadow,transform] t-duration-200 hover:-t-translate-y-0.5 hover:t-border-brand-violet/35 hover:t-shadow-[0_12px_28px_rgba(28,29,31,0.09)]">{t48}{t72}{t73}</div>;
    $[200] = t48;
    $[201] = t72;
    $[202] = t74;
  } else {
    t74 = $[202];
  }
  let t75;
  if ($[203] !== t28 || $[204] !== t74) {
    t75 = <article className="t-h-full t-w-full" itemScope={true} itemType="https://schema.org/Car" itemID={t28}>{t74}</article>;
    $[203] = t28;
    $[204] = t74;
    $[205] = t75;
  } else {
    t75 = $[205];
  }
  return t75;
}
export default function SrpVehicleCard(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = isStaticTileData(props.car) ? <MarketingTile data={props.car} /> : <VehicleCard {...props} car={props.car} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}